import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { toast } from 'sonner';

export interface AssetJobRule {
  id: string;
  shop_id: string;
  job_category_key: string | null;
  job_id: string | null;
  asset_category: string | null;
  asset_id: string | null;
  attribution_mode: 'auto' | 'manual';
  active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Hook for job-to-equipment attribution rules
 * Manager/Writer can manage
 */
export function useAssetJobRules() {
  const { activeShop } = useAuth();
  const { isManager, isServiceWriter } = usePermissions();
  const shopId = activeShop?.id;
  const canManage = isManager || isServiceWriter;

  return useQuery({
    queryKey: ['asset-job-rules', shopId],
    queryFn: async () => {
      if (!shopId) return [];
      
      const { data, error } = await supabase
        .from('shop_asset_job_rules')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as AssetJobRule[];
    },
    enabled: !!shopId && canManage,
  });
}

export function useCreateAssetJobRule() {
  const { activeShop } = useAuth();
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (input: {
      job_category_key?: string;
      job_id?: string;
      asset_category?: string;
      asset_id?: string;
      attribution_mode?: 'auto' | 'manual';
    }) => {
      if (!shopId) throw new Error('No active shop');
      if (!canEditAssets) throw new Error('You do not have permission to manage attribution rules');

      const { data, error } = await supabase
        .from('shop_asset_job_rules')
        .insert({
          shop_id: shopId,
          job_category_key: input.job_category_key || null,
          job_id: input.job_id || null,
          asset_category: input.asset_category || null,
          asset_id: input.asset_id || null,
          attribution_mode: input.attribution_mode || 'auto',
          active: true,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-job-rules'] });
      toast.success('Attribution rule created');
    },
    onError: (error) => {
      toast.error('Failed to create rule', { description: error.message });
    },
  });
}

export function useUpdateAssetJobRule() {
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      id: string;
      job_category_key?: string | null;
      job_id?: string | null;
      asset_category?: string | null;
      asset_id?: string | null;
      attribution_mode?: 'auto' | 'manual';
      active?: boolean;
    }) => {
      if (!canEditAssets) throw new Error('You do not have permission to manage attribution rules');

      const { id, ...updates } = input;

      const { data, error } = await supabase
        .from('shop_asset_job_rules')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-job-rules'] });
      toast.success('Rule updated');
    },
    onError: (error) => {
      toast.error('Failed to update rule', { description: error.message });
    },
  });
}

export function useDeleteAssetJobRule() {
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!canEditAssets) throw new Error('You do not have permission to manage attribution rules');

      const { error } = await supabase
        .from('shop_asset_job_rules')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-job-rules'] });
      toast.success('Rule deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete rule', { description: error.message });
    },
  });
}
